import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'

export type PanelOffset = { x: number; y: number }

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n))
}

/**
 * Drag a fixed panel by its header. First drag switches from CSS default
 * (typically right-docked) to explicit left/top coordinates.
 */
export function usePanelDrag(open: boolean) {
  const [offset, setOffset] = useState<PanelOffset | null>(null)
  const dragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    origX: number
    origY: number
    width: number
    height: number
  } | null>(null)

  useEffect(() => {
    if (!open) setOffset(null)
  }, [open])

  const onHeaderPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return
    const target = event.target
    if (target instanceof Element && target.closest('button, a, input, textarea, select')) {
      return
    }
    const panel = event.currentTarget.closest('.ai-assist')
    if (!(panel instanceof HTMLElement)) return

    const rect = panel.getBoundingClientRect()
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origX: rect.left,
      origY: rect.top,
      width: rect.width,
      height: rect.height,
    }
    setOffset({ x: rect.left, y: rect.top })
    event.currentTarget.setPointerCapture(event.pointerId)
    event.preventDefault()
  }, [])

  const onHeaderPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const dx = event.clientX - drag.startX
    const dy = event.clientY - drag.startY
    const maxX = Math.max(0, window.innerWidth - drag.width)
    const maxY = Math.max(0, window.innerHeight - 48)
    setOffset({
      x: clamp(drag.origX + dx, 0, maxX),
      y: clamp(drag.origY + dy, 0, maxY),
    })
  }, [])

  const onHeaderPointerUp = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }, [])

  return {
    offset,
    dragged: offset != null,
    headerDragHandlers: {
      onPointerDown: onHeaderPointerDown,
      onPointerMove: onHeaderPointerMove,
      onPointerUp: onHeaderPointerUp,
      onPointerCancel: onHeaderPointerUp,
    },
  }
}
